import type { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { configureLocalTableStoreForCloud } from '@infra/persistence/LocalTableStore';

/**
 * Entreprise ACTIVE du poste, lue à chaque appel (jamais capturée au démarrage) :
 * en mode cabinet l'entreprise courante change au fil des bascules.
 */
export function resolveActiveTenantId(ctx: AppContext): string | null {
  return (
    ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
    ctx.tenantContext?.tryGetTenant()?.id ??
    null
  );
}

type ActiveTenantListener = (tenantId: string) => void;

const listeners = new Set<ActiveTenantListener>();

/** S'abonne aux changements d'entreprise active (planificateurs, rejeu de file…). */
export function onActiveTenantChanged(listener: ActiveTenantListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Appelé une fois la bascule terminée (licence, persistance et session rattachées). */
export function emitActiveTenantChanged(tenantId: string): void {
  for (const listener of [...listeners]) {
    try {
      listener(tenantId);
    } catch {
      /* un abonné défaillant ne bloque pas les autres */
    }
  }
}

/**
 * Cloisonne le stockage local sur l'entreprise active dès le démarrage (et non seulement après
 * une bascule) : sans cela, un `list()` sans tenant explicite renvoie toutes les entreprises du poste.
 */
export function scopeLocalTablesToActiveTenant(ctx: AppContext): void {
  const tenantId = resolveActiveTenantId(ctx);
  if (!tenantId) return;
  const mode = ctx.getLicenceRuntime()?.getState()?.claims?.data_mode ?? null;
  configureLocalTableStoreForCloud(isRemoteMultiStationMode(mode), tenantId);
}
